import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HardHat, ClipboardList, CheckCircle2, BarChart3, Sparkles, Rocket, X, Play,
} from "lucide-react";

export const introSeenKey = (email) => `il_intro_seen_v1_${email || "anon"}`;

const SCENE_MS = 10000;

export default function WelcomeIntro({ open, onClose }) {
  const { t } = useTranslation("app");
  const [scene, setScene] = useState(0);
  const timer = useRef(null);

  const scenes = useMemo(() => ([
    { key: "welcome", icon: HardHat },
    { key: "rapportini", icon: ClipboardList },
    { key: "vidima", icon: CheckCircle2 },
    { key: "stats", icon: BarChart3 },
    { key: "boost", icon: Sparkles },
    { key: "go", icon: Rocket },
  ]), []);

  useEffect(() => {
    if (!open) return;
    setScene(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    if (scene < scenes.length - 1) {
      timer.current = setTimeout(() => setScene((s) => s + 1), SCENE_MS);
    }
    return () => clearTimeout(timer.current);
  }, [open, scene, scenes.length]);

  if (!open) return null;

  const next = () => {
    if (scene < scenes.length - 1) setScene(scene + 1);
    else onClose();
  };
  const Icon = scenes[scene].icon;
  const k = scenes[scene].key;
  const isLast = scene === scenes.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950 text-white flex flex-col select-none"
      data-testid="welcome-intro"
      onClick={next}
    >
      <style>{`
        @keyframes introPop { 0% { opacity: 0; transform: translateY(24px) scale(.96); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes introFill { from { width: 0%; } to { width: 100%; } }
        @keyframes introRow { 0% { opacity: 0; transform: translateX(-16px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes introBar { from { height: 8%; } }
        .intro-pop { animation: introPop .7s cubic-bezier(.2,.8,.2,1) both; }
        .intro-pop-2 { animation: introPop .7s .25s cubic-bezier(.2,.8,.2,1) both; }
        .intro-pop-3 { animation: introPop .7s .5s cubic-bezier(.2,.8,.2,1) both; }
        .intro-row { animation: introRow .5s both; }
        .intro-bar { animation: introBar 1.2s cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      {/* progress a segmenti (stile storia) */}
      <div className="flex gap-1.5 p-3 pt-4 px-4">
        {scenes.map((s, i) => (
          <div key={s.key} className="h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
            <div
              className="h-full bg-white rounded-full"
              style={
                i < scene
                  ? { width: "100%" }
                  : i === scene && !isLast
                    ? { animation: `introFill ${SCENE_MS}ms linear both` }
                    : i === scene
                      ? { width: "100%" }
                      : { width: "0%" }
              }
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between px-4">
        <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold flex items-center gap-1.5">
          <Play className="h-3 w-3" /> {t("intro.badge")}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="flex items-center gap-1 text-xs text-white/60 hover:text-white bg-white/10 rounded-full px-3 py-1.5"
          data-testid="intro-skip-btn"
        >
          <X className="h-3.5 w-3.5" /> {t("intro.skip")}
        </button>
      </div>

      {/* scena */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" key={scene}>
        <div className="intro-pop rounded-3xl bg-[#0044FF]/20 border border-[#0044FF]/40 p-5 mb-6">
          <Icon className="h-12 w-12 md:h-16 md:w-16 text-[#4d7dff]" />
        </div>
        <h2 className="intro-pop-2 font-heading font-extrabold text-2xl md:text-4xl max-w-2xl">
          {t(`intro.${k}Title`)}
        </h2>
        <p className="intro-pop-3 text-white/70 text-base md:text-lg mt-3 max-w-xl">
          {t(`intro.${k}Sub`)}
        </p>

        {/* mini-animazioni per scena */}
        {k === "rapportini" && (
          <div className="mt-6 w-full max-w-sm space-y-2 text-left">
            {[0, 1, 2].map((i) => (
              <div key={i} className="intro-row flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 text-sm" style={{ animationDelay: `${0.7 + i * 0.5}s` }}>
                <span className="font-mono text-[#4d7dff]">EG-0{i + 1}-10{i}</span>
                <span className="text-white/60 flex-1">{t("intro.rowOperaio")} {i + 1}</span>
                <span className="font-mono font-bold">8h</span>
              </div>
            ))}
          </div>
        )}
        {k === "vidima" && (
          <div className="mt-6 flex gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="intro-row flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-3 py-1.5 text-sm text-emerald-300" style={{ animationDelay: `${0.8 + i * 0.5}s` }}>
                <CheckCircle2 className="h-4 w-4" /> {t("intro.vidimaBadge")} {i + 1}
              </div>
            ))}
          </div>
        )}
        {k === "stats" && (
          <div className="mt-6 flex items-end gap-3 h-24">
            {[40, 65, 50, 85, 70, 100].map((h, i) => (
              <div key={i} className="intro-bar w-8 rounded-t-md bg-[#0044FF]" style={{ height: `${h}%`, animationDelay: `${0.6 + i * 0.15}s` }} />
            ))}
          </div>
        )}
        {k === "boost" && (
          <div className="intro-pop-3 mt-6 max-w-md border-l-2 border-emerald-400 pl-3 text-left" style={{ animationDelay: "1s" }}>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Daily Boost</div>
            <div className="italic text-white/80 text-sm mt-1">“{t("intro.boostQuote")}”</div>
          </div>
        )}
        {isLast && (
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="intro-pop-3 mt-8 bg-[#0044FF] hover:bg-blue-600 text-white font-bold rounded-full px-8 py-3 text-lg"
            style={{ animationDelay: "0.8s" }}
            data-testid="intro-start-btn"
          >
            {t("intro.startBtn")} 🚀
          </button>
        )}
      </div>

      <div className="pb-5 text-center text-xs text-white/40">
        {t("intro.tapHint")} · {scene + 1}/{scenes.length}
      </div>
    </div>
  );
}
